import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import {
    Box,
    TextField,
    Button,
    Grid2,
    Stack,
    Typography,
    InputAdornment,
    IconButton,
    Table,
    TableBody,
    TableContainer,
    Paper,
    TableHead,
    TableRow,
    TableCell,
    Menu,
    MenuItem,
    useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import { UserRole, useSessionStorage } from "IF/userSession";
import { MessageModal } from "./MessageModal";
import ConfirmationModal from "./ConfirmationModal";
import { convertNumberToFullWidthDigits, createTitle } from "Utils/format";
import { DataContext } from "Contexts/DataProvider";

/** transmission から返すDTO（API: AreaHistoryFromTransmissionRow に合わせる） */
type AreaHistoryFromTransmissionRow = {
    serialNo: string;
    simId: string;
    areaName: string;
    latestDatetime: string; // ISO or "yyyy/MM/dd HH:mm:ss" どちらでもOK（fmtで整形）
};

/** 右側一覧に出す行（現役判定も持つ） */
type SerialListRow = {
    serialNo: string;
    simId: string;
    areaName: string;
    latestDatetime: string;
    use: boolean; // end=9999 の area が存在するなら true（Areas APIから判定）
};

export const SettingAreaHistory = () => {
    // ユーザ情報
    const { getUserData } = useSessionStorage();
    const userData = getUserData();
    const { companyCode, userRole } = userData;

    // サイドバーデータ持ち回り
    const sidebarContext = useContext(DataContext);
    if (!sidebarContext) throw new Error("ChildComponent must be used within an ViewProvider");
    const { menuItems, setChoiceMenu, toggleMenu } = sidebarContext;

    const theme = useTheme();

    // ---- state ----
    const [searchSerialNo, setSearchSerialNo] = useState<string>("");
    const [historyList, setHistoryList] = useState<AreaHistoryFromTransmissionRow[]>([]);
    const [serialList, setSerialList] = useState<SerialListRow[]>([]);

    const [bodyWidth, setBodyWidth] = useState(600);
    const [tableBodyHeight, setTableBodyHeight] = useState(600);

    // messageモーダル
    const [modalOpen, setModalOpen] = useState(false);
    const [message, setMessage] = useState("");
    const closeModal = () => setModalOpen(false);

    // 確認モーダル（未使用だが既存に合わせて残す）
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
    const handleConfirmModalClose = () => setConfirmModalOpen(false);

    // タイトル設定（設定 > エリア管理履歴 の位置に合わせて subItems[2] を想定）
    useEffect(() => {
        if (menuItems.length > 2) {
            const index = UserRole.YKC !== userRole ? 3 : 4;
            document.title = createTitle(menuItems[index]?.subItems?.[2]?.text ?? "エリア管理履歴");
            setChoiceMenu(menuItems[index]?.subItems?.[2]?.text ?? "エリア管理履歴");
            toggleMenu("設定");
        }
    }, [userRole, menuItems, setChoiceMenu, toggleMenu]);

    // 画面サイズ計算 + 右側一覧の初期取得
    useEffect(() => {
        const updateWidth = () => {
            const windowWidth = window.innerWidth;
            const adjustedWidth = windowWidth <= 900 ? windowWidth - 330 : windowWidth - 550;
            setBodyWidth(adjustedWidth);
        };

        const updateHeight = () => {
            const windowHeight = window.innerHeight;
            const calculatedHeight = windowHeight - 130;
            setTableBodyHeight(calculatedHeight);
        };

        updateWidth();
        updateHeight();
        window.addEventListener("resize", updateWidth);
        window.addEventListener("resize", updateHeight);

        // 右側一覧を作る
        getSerialList(companyCode);

        return () => {
            window.removeEventListener("resize", updateWidth);
            window.removeEventListener("resize", updateHeight);
        };
    }, [companyCode]);

    // ---- handlers ----
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchSerialNo(e.target.value.toUpperCase());
    };

    const handleClear = () => {
        setSearchSerialNo("");
        setHistoryList([]);
    };

    const handleSearchButton = async () => {
        if (!searchSerialNo.trim()) {
            setModalOpen(true);
            setMessage("シリアルNoを入力してください。");
            return;
        }
        await fetchHistoryBySerial(companyCode, searchSerialNo);
    };

    // ---- API calls ----

    /** transmission履歴（SerialNo -> SimId はAPI側で解決） */
    const fetchHistoryBySerial = async (schema: string, serialNo: string) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/AreaSetting/GetAreaHistoryFromTransmission`, {
                params: { schema, serialNo },
            });
            setHistoryList(res.data ?? []);
        } catch (error: any) {
            setModalOpen(true);
            if (error?.status === RESPONSE_CODE.BAD_REQUEST) {
                setMessage(error.response?.data ?? "検索に失敗しました。");
            } else {
                setMessage("エリア管理履歴(transmission)の取得に失敗しました。");
            }
            console.error("エリア管理履歴(transmission)の取得に失敗しました。", error);
        }
    };

    /**
     * 右側一覧
     * - Areas API で全areaを取得 → serialNoごとに現役(use=true)判定 & 現在の simId/name を決める
     * - さらに transmission API を serialNoごとに叩くと重いので、
     *   ここでは「最新日時」は空でもOKにしている（必要なら後で一括API化が理想）
     *
     * 今回は「シリアルNo,SIMID,現在エリア名」をまず出したいのでOK。
     */
    const getSerialList = async (schema: string) => {
        try {
            const areasRes = await axios.get(`${API_BASE_URL}/AreaSetting/GetAreas`, {
                params: { schema },
            });

            const areas = areasRes.data ?? [];
            // AreaModel を想定: { serialNo, simId, name, use, startDatetime, ... }

            // serialNoごとに「現役(use=true)」優先、無ければ更新日時/開始日時が新しいものを現在表示にする
            const map = new Map<string, any[]>();
            for (const a of areas) {
                const sn = a.serialNo ?? a.serial_no ?? "";
                if (!sn) continue;
                if (!map.has(sn)) map.set(sn, []);
                map.get(sn)!.push(a);
            }

            const list: SerialListRow[] = Array.from(map.entries()).map(([serialNo, rows]) => {
                const useRow = rows.find((x) => x.use === true);
                const latest = [...rows].sort((a, b) => {
                    const ta = new Date(a.updateDatetime ?? a.update_datetime ?? a.startDatetime ?? a.start_datetime ?? 0).getTime();
                    const tb = new Date(b.updateDatetime ?? b.update_datetime ?? b.startDatetime ?? b.start_datetime ?? 0).getTime();
                    return tb - ta;
                })[0];

                const pick = useRow ?? latest;

                return {
                    serialNo,
                    simId: pick?.simId ?? pick?.sim_id ?? "",
                    areaName: pick?.name ?? "",
                    latestDatetime: "", // 右側に日時まで出したい場合は後で埋める（下のコメント参照）
                    use: !!useRow,
                };
            });

            // 現役を上、非現役を下、同じなら serialNo順
            list.sort((a, b) => {
                if (a.use !== b.use) return a.use ? -1 : 1;
                return a.serialNo.localeCompare(b.serialNo);
            });

            setSerialList(list);
        } catch (error) {
            console.error("シリアル一覧の取得に失敗しました:", error);
        }
    };

    // ---- mobile menu ----
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleSelect = async (serialNo: string) => {
        setSearchSerialNo(serialNo);
        setAnchorEl(null);
        await fetchHistoryBySerial(companyCode, serialNo);
    };

    // ---- scroll (right list) ----
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
    const [highlightedSerialNo, setHighlightedSerialNo] = useState<string | null>(null);

    useLayoutEffect(() => {
        if (!searchSerialNo) return;

        const m = serialList.find((x) => x.serialNo.toLowerCase().startsWith(searchSerialNo.toLowerCase()));
        const matched = m?.serialNo ?? searchSerialNo;

        const row = rowRefs.current[matched];
        const container = tableContainerRef.current;
        if (row && container) {
            requestAnimationFrame(() => {
                row.scrollIntoView({ behavior: "smooth", block: "center" });
            });
            setHighlightedSerialNo(matched);
        }
    }, [searchSerialNo, serialList]);

    // 日時表示（LatestDatetime用）
    const fmt = (dt: any) => {
        if (!dt) return "";
        const d = dt instanceof Date ? dt : new Date(dt);
        if (isNaN(d.getTime())) return String(dt);

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        const ss = String(d.getSeconds()).padStart(2, "0");
        return `${yyyy}/${mm}/${dd} ${hh}:${mi}:${ss}`;
    };

    return (
        <>
            <MessageModal open={modalOpen} message={message} handleClose={closeModal} />
            <ConfirmationModal
                open={confirmModalOpen}
                message={confirmMessage}
                handleClose={handleConfirmModalClose}
                onConfirm={onConfirm!}
            />

            <Box sx={{ display: "flex", width: "100%" }}>
                {/* 左：検索＋結果 */}
                <Box sx={{ flex: 7 }}>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                            <TextField
                                fullWidth
                                label="シリアルNo"
                                name="searchSerialNo"
                                value={searchSerialNo}
                                onChange={handleInputChange}
                                inputProps={{ maxLength: 6 }}
                                InputProps={{
                                    endAdornment: searchSerialNo && (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClear} edge="end">
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Grid2>※シリアルNoを入力して検索してください。</Grid2>
                        </Grid2>

                        <Grid2
                            container
                            sx={{
                                gap: 2,
                                justifyContent: { xs: "center", sm: "flex-end", md: "flex-end", lg: "flex-start" },
                                alignItems: "flex-start",
                                pt: { lg: 1 },
                            }}
                            size={{ xs: 12, sm: 12, md: 12, lg: 6 }}
                        >
                            <Grid2 sx={{ display: "flex", alignItems: "center" }}>
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    sx={{ width: { xs: "20vh", sm: "30vh" } }}
                                    onClick={handleSearchButton}
                                >
                                    検索／絞り込み
                                </Button>
                            </Grid2>

                            {/* モバイル用（一覧から選択） */}
                            <Grid2 sx={{ display: { md: "flex", lg: "none" }, alignItems: "center" }}>
                                <Button variant="outlined" onClick={handleClick} sx={{ width: { xs: "20vh", sm: "30vh" } }}>
                                    シリアル一覧から選ぶ
                                </Button>
                                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                                    {serialList.map((x) => (
                                        <MenuItem
                                            key={x.serialNo}
                                            onClick={() => handleSelect(x.serialNo)}
                                            sx={{ color: x.use ? "inherit" : "rgba(0,0,0,0.45)" }}
                                        >
                                            {x.serialNo}（{x.areaName}）
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Grid2>
                        </Grid2>

                        {/* 結果 */}
                        <Stack direction={{ xs: "column" }} spacing={2} sx={{ flexGrow: 1 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    p: 1,
                                    border: `4px solid ${theme.palette.custom.paperBorderColorSub}`,
                                    borderRadius: "8px",
                                    backgroundColor: theme.palette.custom.sForm,
                                }}
                            >
                                <Typography fontWeight="fontWeightBold">エリア管理履歴</Typography>

                                <TableContainer component={Paper} sx={{ backgroundColor: theme.palette.custom.row }}>
                                    <Table sx={{ width: "100%", tableLayout: "fixed" }}>
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    "& th": {
                                                        backgroundColor: theme.palette.custom.tableHeader,
                                                        color: "white",
                                                        fontWeight: "bold",
                                                    },
                                                }}
                                            >
                                                <TableCell sx={{ width: "18%" }}>シリアルNo</TableCell>
                                                <TableCell sx={{ width: "28%" }}>SIMID</TableCell>
                                                <TableCell sx={{ width: "25%" }}>エリア名</TableCell>
                                                <TableCell sx={{ width: "29%" }}>運用開始日</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {historyList.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ textAlign: "center" }}>
                                                        検索結果がありません。
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                historyList.map((r, idx) => (
                                                    <TableRow key={`${r.simId}-${idx}`}>
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{r.serialNo}</TableCell>
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{r.simId}</TableCell>
                                                        <TableCell sx={{ wordBreak: "break-word" }}>{r.areaName}</TableCell>
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(r.latestDatetime)}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Stack>
                    </Grid2>
                </Box>

                {/* 右：一覧（PCのみ） */}
                <Box sx={{ flex: 2, p: 2, display: { xs: "none", md: "none", lg: "block" } }}>
                    <TableContainer
                        sx={{
                            maxHeight: tableBodyHeight,
                            minHeight: tableBodyHeight,
                            overflowY: "auto",
                            border: `4px solid ${theme.palette.custom.paperBorderColorSub}`,
                            borderRadius: "8px",
                            backgroundColor: theme.palette.custom.row,
                        }}
                        ref={tableContainerRef}
                    >
                        <Table>
                            <TableHead
                                sx={{
                                    position: "sticky",
                                    top: 0,
                                    backgroundColor: theme.palette.custom.tableHeader,
                                    zIndex: 1,
                                }}
                            >
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ textAlign: "center", color: "white" }}>
                                        <Typography fontWeight="fontWeightBold">
                                            シリアル一覧（{convertNumberToFullWidthDigits(serialList.length)}）
                                        </Typography>
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell
                                        size="small"
                                        sx={{
                                            textAlign: "center",
                                            color: "white",
                                            fontSize: "0.8rem",
                                            padding: "6px",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        シリアルNo
                                    </TableCell>
                                    <TableCell size="small" sx={{ textAlign: "center", color: "white" }}>
                                        SIMID
                                    </TableCell>
                                    <TableCell size="small" sx={{ textAlign: "center", color: "white" }}>
                                        エリア名
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {serialList.map((x) => (
                                    <TableRow
                                        key={x.serialNo}
                                        onClick={() => handleSelect(x.serialNo)}
                                        sx={{
                                            cursor: "pointer",
                                            backgroundColor:
                                                x.serialNo === highlightedSerialNo
                                                    ? theme.palette.custom.check
                                                    : x.use
                                                        ? theme.palette.custom.row
                                                        : "rgba(0,0,0,0.08)", // 非現役はグレー
                                            color: x.use ? "inherit" : "rgba(0,0,0,0.45)",
                                        }}
                                        ref={(el) => (rowRefs.current[x.serialNo] = el)}
                                    >
                                        <TableCell sx={{ textAlign: "center", whiteSpace: "nowrap" }}>{x.serialNo}</TableCell>
                                        <TableCell sx={{ textAlign: "center", whiteSpace: "nowrap" }}>{x.simId}</TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>{x.areaName}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </>
    );
};

export default SettingAreaHistory;